import { ref, watchEffect } from 'vue'

import {
  normalizeUrlMatchingRule,
  serializeUrlMatchingRulePayload,
} from '@/lib/adapters/url-matching-rules.adapter'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { request } from '@/lib/services'
import type {
  UrlMatchingRulePayloadType,
  UrlMatchingRuleType,
} from '@/lib/types/url-matching-rules.types'
import { useErrorStore } from '@/stores/error'

export const useGetUrlMatchingRules = (advertiserId: number) => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const data = ref<UrlMatchingRuleType[]>([])
  const errorStore = useErrorStore()

  const getUrlMatchingRules = async () => {
    try {
      isLoading.value = true
      error.value = null
      const response = await request.get(`/v2/advertisers/${advertiserId}/url-matching-rules`)

      data.value = response.data.data.map(normalizeUrlMatchingRule)
    } catch (e) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  watchEffect(() => void getUrlMatchingRules())

  return {
    data,
    getUrlMatchingRules,
    isLoading,
    error,
  }
}

export const useCreateUrlMatchingRule = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const createUrlMatchingRule = async (advertiserId: number, data: UrlMatchingRulePayloadType) => {
    try {
      isLoading.value = true
      error.value = null
      const response = await request.post(
        `/v2/advertisers/${advertiserId}/url-matching-rules`,
        serializeUrlMatchingRulePayload(data),
      )
      return normalizeUrlMatchingRule(response.data)
    } catch (e) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  return {
    createUrlMatchingRule,
    isLoading,
    error,
  }
}

export const useEditUrlMatchingRule = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const editUrlMatchingRule = async (
    advertiserId: number,
    ruleId: number,
    data: UrlMatchingRulePayloadType,
  ) => {
    try {
      isLoading.value = true
      error.value = null
      const response = await request.put(
        `/v2/advertisers/${advertiserId}/url-matching-rules/${ruleId}`,
        serializeUrlMatchingRulePayload(data),
      )
      return normalizeUrlMatchingRule(response.data)
    } catch (e) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  return {
    editUrlMatchingRule,
    isLoading,
    error,
  }
}

export const useDeleteUrlMatchingRule = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const deleteUrlMatchingRule = async (advertiserId: number, id: number) => {
    try {
      isLoading.value = true
      error.value = null
      await request.delete(`/v2/advertisers/${advertiserId}/url-matching-rules/${id}`)
    } catch (e) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  return {
    deleteUrlMatchingRule,
    isLoading,
    error,
  }
}
